<template>
  <teleport to="#imageEnlargedTarget" :disabled="!isEnlarged">
    <span v-if="loading" class="loading" :class="{ styled }">
      <Spinner />
    </span>
    <div
      v-else-if="fileUrl || url"
      class="h-full flex w-full"
      :class="{ 'justify-center': !isEnlarged }"
      :style="`--aspect:${aspect};--rotation:${totalRotation ?? 0}deg`"
    >
      <div class="max-w-full max-h-full image-wrapper relative">
        <img
          ref="imgRef"
          :alt="alt"
          :class="{
            enlarged: isEnlarged,
            'pointer-events-none': (rotationEnabled || initialRotation !== 0) && !isEnlarged,
            'rotation-enabled': rotationEnabled || initialRotation !== 0,
            'cursor-pointer': enlargeable,
            'r-90': totalRotation === 90,
            'r-180': totalRotation === 180,
            'r-270': totalRotation === 270,
          }"
          :src="displayUrl"
          :title="title"
          class="h-full object-contain"
          draggable="true"
          v-bind="$attrs"
          @click="close()"
          @mousedown="onMouseDown"
          @dragstart="onDragStart"
          @dragend="restoreDisplaySrc"
          @mouseup="restoreDisplaySrc"
          @load="setAspect"
        >
      </div>
      <div
        class="absolute h-full w-full img-overlay"
        :class="{ 'pointer-events-none': isEnlarged }"
      >
        <div
          v-if="enlargeable && !isEnlarged && !loading && !disabled"
          class="absolute h-full w-full flex flex-col justify-center enlargeOverlay cursor-pointer"
          @click="isEnlarged = enlargeable && !isEnlarged"
        >
          <div class="enlargeIcon">
            <Enlarge />
          </div>
        </div>
        <div v-if="rotationEnabled && !isEnlarged" class="rotationOverlay">
          <div class="flex justify-between gap-x-2">
            <IconCCW class="w-8 h-8 cursor-pointer hoverPathRed" @click="rotate('ccw')" />
            <IconCW class="w-8 h-8 cursor-pointer hoverPathRed" @click="rotate('cw')" />
            <SaveIcon
              v-if="!savingRotation"
              class="w-8 h-8 ml-5 saveIcon"
              :class="{
                disabled: rotation === 0,
                'cursor-pointer': rotation !== 0,
              }"
              @click="saveRotation"
            />
            <Spinner v-else class="w-8 h-8 ml-5 mt-1 mr-1" />
          </div>
        </div>
      </div>
    </div>
    <span v-else class="error" :class="{ styled }">
      <Cross />
    </span>
  </teleport>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import Cross from '@images/close.svg?component';
import Enlarge from '@images/enlarge.svg?component';
import IconCCW from '@images/rotate-ccw.svg?component';
import IconCW from '@images/rotate-cw.svg?component';
import SaveIcon from '@images/save.svg?component';
import { useNotify } from '@utils/notifications';
import { useI18n } from 'vue-i18n';
import { useFetchAction } from '@hooks/fetch';
import { useBlob, useDataUrl, useObjectUrl } from '@/hooks/file';
import Spinner from './Spinner.vue';
import type { WbFile } from '@/entities/Entities';

interface Props {
  src?: string;
  file?: Blob;
  busy?: boolean;
  alt: string;
  styled?: boolean;
  enlargeable?: boolean;
  title?: string;
  disabled?: boolean;
  rotationEnabled?: boolean;
  fileEntity?: WbFile;
  initialRotation?: number;
}

const props = defineProps<Props>();
const emits = defineEmits(['save:rotation']);
const { blob: srcBlob, loading: srcLoading } = useBlob(computed(() => props.src));
const loading = computed(() => props.busy || srcLoading.value);
const url = useObjectUrl(srcBlob);
const fileUrl = useObjectUrl(computed(() => props.file));

const isEnlarged = ref(false);
const imgRef = ref<HTMLImageElement>();

// The cheap `blob:` URL of the underlying file — size-independent, draggable at any resolution.
const blobSrc = computed(() => (fileUrl.value || url.value) || undefined);

// WOR-290: the enlarged <img> renders from a forced `data:image/png` URL so right-click
// "Save image as…" suggests a real `download.png` (a `blob:` URL would save a bare object-URL UUID
// with no extension). Only the fullscreen image is ever base64-encoded.
const { dataUrl: enlargedDataUrl } = useDataUrl(
  () => (isEnlarged.value ? (props.file ?? srcBlob.value) : undefined),
  'image/png',
);

// Resting display source: `data:` when enlarged (for right-click-save filename), `blob:` otherwise.
const displayUrl = computed(() => {
  if (isEnlarged.value && enlargedDataUrl.value) return enlargedDataUrl.value;
  return blobSrc.value;
});

// Chromium aborts dragging a large `data:` URL image, but right-click-save needs that `data:`
// source for a filename. For a LEFT-button press, swap the live DOM `src` to the cheap `blob:` URL
// before `dragstart` captures it (direct write — Vue's reactive `:src` is async), then restore the
// `data:` source on drag end / mouse up. Right-click never swaps, so "Save image as…" sees `data:`.
const restoreDisplaySrc = () => {
  if (imgRef.value && displayUrl.value) imgRef.value.src = displayUrl.value;
};
const onMouseDown = (e: MouseEvent) => {
  if (!imgRef.value) return;
  if (e.button === 0 && isEnlarged.value && blobSrc.value) {
    imgRef.value.src = blobSrc.value;
  } else {
    restoreDisplaySrc();
  }
};

const close = () => {
  if (props.enlargeable && isEnlarged.value) {
    isEnlarged.value = false;
  }
};

// Chromium will not drag an image sourced from a `blob:` URL out to the OS filesystem unless the
// page explicitly declares the drag payload via the `DownloadURL` data-transfer entry. Firefox
// drags the blob image natively without this, so setting it is a harmless no-op there. onMouseDown
// has already swapped the live <img> to the cheap `blob:` source so `dragstart` actually fires for
// large photos (Chromium aborts dragging a big `data:` image); the `DownloadURL` below then points
// Chromium at the anonymous http content URL so the dropped file gets the real name + extension.
const MIME_EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
  'image/bmp': 'bmp',
  'image/tiff': 'tiff',
  'application/pdf': 'pdf',
};

const extFromMime = (mime: string) => MIME_EXTENSIONS[mime.toLowerCase()] ?? 'bin';

const onDragStart = (e: DragEvent) => {
  if (!e.dataTransfer) {
    return;
  }
  const id = props.fileEntity?.id;
  // Chromium's `DownloadURL` drag-out names the dropped file from the URL field — but only honors
  // the filename for a real http(s) URL; a `blob:`/`data:` URL falls back to a generic "Download".
  // The legacy `/files/{id}/content` endpoint is anonymous (no bearer needed for the OS-level
  // download fetch), so we point at that fully-qualified URL. `contentUrl` is preferred when the
  // payload includes it; otherwise we rebuild the stable legacy path from the id. Falls back to the
  // blob: URL only when there's no file entity at all (drag still works, name may be generic).
  const contentPath = props.fileEntity?.contentUrl || (id ? `/files/${id}/content` : undefined);
  let downloadUrl: string | undefined;
  if (contentPath) {
    downloadUrl = /^https?:/i.test(contentPath)
      ? contentPath
      : `${import.meta.env.VITE_API_URL ?? ''}${contentPath}`;
  }
  downloadUrl = downloadUrl || fileUrl.value || url.value || undefined;
  if (!downloadUrl) {
    return;
  }
  const sourceBlob = props.file ?? srcBlob.value;
  const mime = props.fileEntity?.mimeType || sourceBlob?.type || 'application/octet-stream';
  const originalName = props.fileEntity?.originalFileName;
  // Build "<base>_<id>.<ext>" — append the file id before the extension when we have it, so the
  // saved file is traceable to the record. Falls back gracefully when id/originalName is absent.
  let filename: string;
  if (originalName && originalName.includes('.')) {
    const dot = originalName.lastIndexOf('.');
    const base = originalName.slice(0, dot);
    const fileExt = originalName.slice(dot + 1);
    filename = id ? `${base}_${id}.${fileExt}` : originalName;
  } else {
    const ext = extFromMime(mime);
    filename = id ? `image_${id}.${ext}` : `image.${ext}`;
  }
  e.dataTransfer.setData('DownloadURL', `${mime}:${filename}:${downloadUrl}`);
};

const rotation = ref(0);
const baseRotation = ref(0);

const totalRotation = computed(() => (rotation.value + baseRotation.value) % 360);

const aspect = ref();

const rotate = (direction: 'cw' | 'ccw') => {
  if (!props.rotationEnabled) {
    return;
  }
  if (direction === 'cw') {
    rotation.value = (rotation.value + 90) % 360;
  } else {
    rotation.value = (rotation.value + 270) % 360;
  }
};

watch(
  [() => props.src, () => props.file],
  () => {
    baseRotation.value = props.initialRotation ?? 0;
    rotation.value = 0;
    aspect.value = undefined;
  },
  { immediate: true },
);

const setAspect = () => {
  if (imgRef.value && aspect.value === undefined) {
    const newImg = new Image();
    newImg.src = displayUrl.value || '';

    newImg.onload = () => {
      if (newImg.naturalHeight && newImg.naturalWidth) {
        aspect.value = newImg.naturalWidth / newImg.naturalHeight;
      }
    };
  }
};

const notify = useNotify();

const { t } = useI18n();

const [exec, { loading: savingRotation }] = useFetchAction();

const saveRotation = async () => {
  if (rotation.value === 0) {
    return;
  }
  emits('save:rotation', rotation.value);
  if (props.fileEntity?.['@id']) {
    try {
      await exec('/files/rotate', {
        method: 'PUT',
        body: {
          file: props.fileEntity['@id'],
          rotation: rotation.value,
        },
      });

      notify?.({
        variant: 'success',
        title: t('image.rotation.saveSuccess'),
      });
    } catch (e) {
      notify?.({
        variant: 'error',
        title: t('image.rotation.saveError'),
      });
      return;
    }
  }
  baseRotation.value = totalRotation.value;
  rotation.value = 0;
};
</script>

<style lang="scss" scoped>
.loading {
  @apply w-full h-full;
  @apply flex items-center justify-center;
}

.error {
  @apply w-full h-full;
  @apply flex items-center justify-center;

  :deep(svg) {
    @apply w-5 h-5;
  }
}

.styled {
  @apply border border-element-neutral bg-background-light;
  @apply rounded-4;
}

.enlarged {
  position: fixed;
  object-fit: contain;
  padding-top: 5vh;
  padding-bottom: 5vh;
  background: rgba(0, 0, 0, 0.5);

  height: 100vh;
  width: 100vw;

  &.rotation-enabled {
    &.r-90,
    &.r-270 {
      width: 100vh;
      height: 100vw;
    }
  }
}

.enlargeIcon {
  width: min(10rem, 100%);
  height: min(10rem, 100%);
  margin: auto;
  display: none;
}

.enlargeOverlay:hover .enlargeIcon {
  display: initial;
}

.rotationOverlay {
  @apply absolute top-0 right-0 bg-light-gray p-2 rounded-4 select-none;
  opacity: 0.2;
}

.img-overlay:hover .rotationOverlay {
  opacity: 1;
}

.hoverPathRed:hover {
  :deep(path) {
    stroke: var(--color-primary);
  }
}

.saveIcon {
  :deep(path) {
    fill: black;
  }

  &:hover {
    :deep(path) {
      fill: var(--color-primary);
    }
  }

  &.disabled {
    :deep(path) {
      fill: gray;
    }
    &:hover {
      :deep(path) {
        fill: gray;
      }
    }
  }
}

.rotation-enabled {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(var(--rotation));

  &.r-90,
  &.r-270 {
    width: calc(var(--aspect) * 100%);
    height: calc(1 / var(--aspect) * 100%);
  }
}

.image-wrapper {
  aspect-ratio: var(--aspect);
}
</style>
